import { useTranslation } from 'react-i18next'
import { AppIcon } from '../ui/icon'
import { mdiLinkVariant } from '@mdi/js'
import { cn } from '../../lib/utils'

interface AssetRelationsBadgeProps {
    /** `link_counts.relations` — active typed edges on either side. */
    count?: number | null
    className?: string
}

/**
 * Link icon + count of typed asset relations, shown wherever assets are
 * *browsed* (tree rows, lists, graph nodes) so a desserte link is visible
 * without opening the asset (spec 2026-08-16-03 §6).
 *
 * Renders nothing when the count is zero or the caller didn't request
 * `?include=link_counts` — an empty badge would just be noise.
 */
export function AssetRelationsBadge({ count, className }: AssetRelationsBadgeProps) {
    const { t } = useTranslation('assets')
    if (!count || count <= 0) return null

    const label = t('relations.badge_tooltip', {
        count,
        defaultValue: '{{count}} linked asset(s)',
    })

    return (
        <span
            className={cn(
                'inline-flex items-center gap-0.5 rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground',
                className,
            )}
            data-testid="asset-relations-badge"
            data-relations-count={count}
            aria-label={label}
            title={label}
        >
            <AppIcon path={mdiLinkVariant} size="xs" aria-hidden />
            <span>{count > 99 ? '99+' : count}</span>
        </span>
    )
}
